import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Search,
  Filter,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { formatLegislatorNameFromObject } from '../utils/nameUtils';
import PageShell from '../components/PageShell';
import Pagination from '../components/Pagination';
import StatCard, { StatCardRow } from '../components/StatCard';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../components/StandardTable';

interface Legislator {
  id: number;
  full_name: string;
  first_name?: string;
  last_name?: string;
}

interface LegislatorVote {
  vote: string;
  vote_date: string;
  bill_id: number;
  bill_number: string;
  bill_title: string;
  bill_summary?: string;
  chamber: string;
  action: string;
  motion: string;
  chamber_vote_date: string;
  vote_context: string;
}

interface VoteCategory {
  key: string;
  label: string;
  count: number;
  percent: number;
}

interface VoteSummary {
  totalVotes: number;
  categories: VoteCategory[];
}

const VOTE_STAT_VARIANTS: Record<string, 'success' | 'danger' | 'warning' | 'default'> = {
  yes: 'success',
  no: 'danger',
  excused: 'warning',
  absent: 'default',
  other: 'default',
};

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface VotesData {
  legislator: Legislator;
  votes: LegislatorVote[];
  pagination: Pagination;
  summary: VoteSummary;
  /** Sessions this legislator has votes in, newest first */
  sessions: string[];
  filters: {
    vote_type: string;
    context: string;
    chamber: string;
    search: string;
    sort: string;
    /** The session actually applied server-side ('All' when unfiltered) */
    session: string;
  };
}

const VOTE_BADGE_STYLES: Record<string, string> = {
  Y: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700',
  N: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700',
  E: 'bg-background-tertiary text-neutral-800 border-border-light-secondary dark:bg-background-dark-secondary dark:text-text-dark-secondary dark:border-border-dark-secondary',
  V: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-700',
};

const VOTE_LABELS: Record<string, string> = { Y: 'Yes', N: 'No', E: 'Excused', V: 'Absent' };

const CONTEXT_BADGE_STYLES: Record<string, string> = {
  'Final Reading': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
  'Committee': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  'Appropriations': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  'Amendment': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300',
};

const LegislatorVotesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<VotesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [voteType, setVoteType] = useState(searchParams.get('vote_type') || 'All');
  const [context, setContext] = useState(searchParams.get('context') || 'All');
  const [chamber, setChamber] = useState(searchParams.get('chamber') || 'All');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'date');
  // '' = no explicit choice: the server defaults to the current (newest) session
  const [session, setSession] = useState(searchParams.get('session') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));

  const fetchVotes = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        vote_type: voteType,
        context: context,
        chamber: chamber,
        search: search,
        sort: sort
      });
      if (session) params.set('session', session);

      const response = await api.get(`/legislators/${id}/votes?${params}`);
      
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to fetch voting history');
      }
    } catch (err) {
      console.error('Error fetching votes:', err);
      setError('Error loading voting history');
    } finally {
      setLoading(false);
    }
  };

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (voteType !== 'All') params.set('vote_type', voteType);
    if (context !== 'All') params.set('context', context);
    if (chamber !== 'All') params.set('chamber', chamber);
    if (search) params.set('search', search);
    if (sort !== 'date') params.set('sort', sort);
    if (session) params.set('session', session);
    if (page !== 1) params.set('page', page.toString());

    // Replace (not push) so filter-driven URL updates don't add history entries.
    // Pushing here inserted an extra entry on mount, which broke the header Back
    // button (it would return to this same page instead of the previous page).
    setSearchParams(params, { replace: true });
  }, [voteType, context, chamber, search, sort, session, page, setSearchParams]);

  // Fetch data when filters change
  useEffect(() => {
    fetchVotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, voteType, context, chamber, search, sort, session, page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on search
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-neutral-600 dark:text-text-dark-tertiary">Loading voting history...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
            {error}
          </h2>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <PageShell
      title={`${formatLegislatorNameFromObject(data.legislator)} — Voting History`}
      description="Complete voting record with context and bill details"
    >
      {/*
        The bespoke header bar and min-h-screen wrapper are gone. Both duplicated
        what Layout already provides — its <main> supplies the page background
        and height, and its header carries the title.
      */}
      <div className="max-w-7xl mx-auto">
        {/* Filters */}
        <div className="card-hairline p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-neutral-500" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Session Filter - defaults server-side to the current session */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Session
              </label>
              <select
                value={session || data.filters.session}
                onChange={(e) => { setSession(e.target.value); setPage(1); }}
                className="input"
              >
                {data.sessions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                <option value="All">All Sessions</option>
              </select>
            </div>

            {/* Recorded Vote Filter (vote_type value) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Recorded Vote
              </label>
              <select
                value={voteType}
                onChange={(e) => { setVoteType(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="All">All Votes</option>
                <option value="Y">Yes</option>
                <option value="N">No</option>
                <option value="E">Excused</option>
                <option value="V">Absent</option>
              </select>
            </div>

            {/* Vote Type Filter (context value) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Vote Type
              </label>
              <select
                value={context}
                onChange={(e) => { setContext(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="All">All Contexts</option>
                <option value="Final Reading">Final Reading</option>
                <option value="Committee">Committee</option>
                <option value="Appropriations">Appropriations</option>
                <option value="Amendment">Amendment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Chamber Filter */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Chamber
              </label>
              <select
                value={chamber}
                onChange={(e) => { setChamber(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="All">All Chambers</option>
                <option value="House">House</option>
                <option value="Senate">Senate</option>
              </select>
            </div>

            {/* Sort Filter */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Sort By
              </label>
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                className="input"
              >
                <option value="date">Date (Newest)</option>
                <option value="bill_number">Bill Number</option>
                <option value="vote">Vote Type</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
                Search Bills
              </label>
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Bill number or title..."
                  className="input pr-10"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-500 hover:text-neutral-600 dark:hover:text-text-dark-secondary"
                >
                  <Search className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="card-hairline p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="w-5 h-5 text-neutral-500" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Summary ({data.summary.totalVotes} votes)
            </h3>
          </div>
          
          <StatCardRow>
            {data.summary.categories.map((cat) => (
              <StatCard
                key={cat.key}
                value={`${cat.percent}%`}
                label={`${cat.label} (${cat.count})`}
                variant={VOTE_STAT_VARIANTS[cat.key] ?? 'default'}
              />
            ))}
            <StatCard value={data.summary.totalVotes} label="Total Votes" variant="info" />
          </StatCardRow>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="card-hairline p-8">
            <div className="flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-3" />
              <span className="text-neutral-600 dark:text-text-dark-tertiary">Loading votes...</span>
            </div>
          </div>
        )}

        {/* Results table */}
        {!loading && data.votes.length > 0 && (
          <StandardTable>
            <StandardTableHeader>
              <tr>
              <StandardTableHeaderCell columnType="nowrap">Date</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="nowrap">Bill</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="expand">Title</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Vote</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Type</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" responsiveHide="lg">Chamber</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Motion</StandardTableHeaderCell>
              </tr>
            </StandardTableHeader>
            <StandardTableBody>
              {data.votes.map((vote, index) => (
                <StandardTableRow key={`${vote.bill_id}-${vote.chamber_vote_date}-${index}`} hoverable>
                  <StandardTableCell className="whitespace-nowrap">
                    {vote.chamber_vote_date}
                  </StandardTableCell>
                  <StandardTableCell className="whitespace-nowrap">
                    <Link
                      to={`/legislation/${encodeURIComponent(vote.bill_number)}`}
                      className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                    >
                      {vote.bill_number}
                    </Link>
                  </StandardTableCell>
                  <StandardTableCell>{vote.bill_title}</StandardTableCell>
                  <StandardTableCell>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${VOTE_BADGE_STYLES[vote.vote] ?? VOTE_BADGE_STYLES.E}`}>
                      {VOTE_LABELS[vote.vote] ?? vote.vote}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${CONTEXT_BADGE_STYLES[vote.vote_context] ?? 'bg-background-tertiary text-neutral-800 dark:bg-background-dark-secondary dark:text-text-dark-secondary'}`}>
                      {vote.vote_context}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{vote.chamber}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">
                    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      {vote.motion || vote.action || '—'}
                    </span>
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}

        {/* No Results */}
        {!loading && data.votes.length === 0 && (
          <div className="card-hairline p-8">
            <div className="text-center text-neutral-500 dark:text-text-dark-tertiary">
              No votes found matching your criteria.
            </div>
          </div>
        )}

        {/* Pagination */}
        {data.pagination.totalPages > 1 && (
          <Pagination
            className="mt-8"
            page={data.pagination.page}
            pageCount={data.pagination.totalPages}
            total={data.pagination.total}
            pageSize={data.pagination.limit}
            noun="votes"
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </PageShell>
  );
};

export default LegislatorVotesPage;
